import { FunctionComponent, memo, useEffect, useMemo } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ISubscribedMap } from "./ISubscribed";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addSubscribedMap } from "../../Redux/Slices/SubscribedMap/AddSubscribedMapSlice";
import { EditSubscribedMap } from "../../Redux/Slices/SubscribedMap/EditSubscribedMapSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

interface SubscribedMapFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: ISubscribedMap;
  setInitialValues: React.Dispatch<React.SetStateAction<ISubscribedMap>>;
}

const SubscribedMapForm: FunctionComponent<SubscribedMapFormProps> = (
  props
) => {
  const dispatch = useAppDispatch();

  const userSchema = z.object({
    id: z.number().optional(),
    package_id: z.number({
      required_error: "Please select package",
      invalid_type_error: "Please select package",
    }),
    user_id: z.number({
      required_error: "Please select user name",
      invalid_type_error: "Please select user name",
    }),
    status: z.boolean({
      message: "Please select the status",
    }),
    end_date: z
      .preprocess(
        (arg) =>
          typeof arg === "string" || arg instanceof Date ? dayjs(arg) : arg,
        z.custom<Dayjs>((val) => dayjs.isDayjs(val) && val.isValid(), {
          message: "Please select a valid end date",
        })
      )
      .refine((val) => val && val.isAfter(dayjs()), {
        message: "Date must be in the future",
      }),
  });

  const methods = useForm<ISubscribedMap>({
    defaultValues: {
      ...props.initialValues,
      end_date: props.initialValues.end_date
        ? dayjs(props.initialValues.end_date)
        : null,
      status: props.initialValues.id ? props.initialValues.status : true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<any> = (val) => {
    const formattedData: ISubscribedMap = {
      ...val,
      end_date: val.end_date ? dayjs(val.end_date).format("YYYY-MM-DD") : null,
    };

    if (formattedData.id) {
      dispatch(EditSubscribedMap(formattedData));
    } else {
      dispatch(addSubscribedMap(formattedData));
    }
  };

  useEffect(() => {
    return () => {
      props.setInitialValues({} as ISubscribedMap);
    };
  }, []);

  const { data: listSubscribedMaps } = useAppSelector(
    (state) => state.ListSubscribedMapSlice
  );
  const listUsers = useAppSelector((state) => state.ListUserAdminSlice);
  const ListPackagespage = useAppSelector((state) => state.ListPackageSlice);

  interface PackageItem {
    name: string;
    admin_only: boolean;
    id: number;
  }

  const Packagelist =
    (ListPackagespage?.data?.data as PackageItem[] | undefined)
      ?.filter((pkg) => pkg.admin_only !== false)
      .map((pkg) => ({
        label: pkg.name!,
        value: pkg.id,
      })) || [];

  interface UserItem {
    username: string;
    status: boolean;
    id: number;
    mapped_user?: string;
  }

const alreadySubscribedUsers= listSubscribedMaps?.data?.map((item: any) => ({
  id: item.user_id,
  name: item.user_name,
}));

console.log(alreadySubscribedUsers)
  const selectedPackageId = methods.watch("package_id");
  const selectedUserId = methods.watch("user_id");

const filteredUsers = useMemo(() => {
  // Start with all active users
  let users =
    (listUsers?.data?.data as UserItem[] | undefined)
      ?.filter((user) => user.status !== false)
      .map((user) => ({
        label: user.mapped_user ?? user.username,
        value: user.id,
      })) || [];

  // Get all already subscribed user IDs
  const subscribedMaps = listSubscribedMaps?.data ?? [];
  const alreadySubscribedUserIds = Array.isArray(subscribedMaps)
    ? subscribedMaps.map((map: ISubscribedMap) => map.user_id)
    : [];

  // Exclude already subscribed users, but allow selectedUserId to stay
  users = users.filter(
    (user) => !alreadySubscribedUserIds.includes(user.value) || user.value === selectedUserId
  );

  return users;
}, [selectedUserId, listUsers, listSubscribedMaps]);



  // Status list
  const RoleSatus = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_content"
      >
        <Select
          name="package_id"
          label="Select the package"
          options={Packagelist ?? []}
          value={
            Packagelist?.find(
              (list: any) => list.value === methods.getValues("package_id")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("package_id", val?.value, {
              shouldValidate: true,
            });
          }}
        />
        <Select
          name="user_id"
          label="Select the Student"
          options={filteredUsers}
          value={
            filteredUsers.find(
              (list) => list.value === methods.getValues("user_id")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("user_id", val?.value, {
              shouldValidate: true,
            });
          }}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]}>
            <Controller
              name="end_date"
              control={methods.control}
              render={({ field, fieldState }) => (
                <DatePicker
                  label="End Date"
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  minDate={dayjs().add(1, "day")}
                  slotProps={{
                    textField: {
                      helperText: fieldState.error?.message,
                      error: !!fieldState.error,
                      fullWidth: true,
                    },
                  }}
                />
              )}
            />
          </DemoContainer>
        </LocalizationProvider>

        <Select
          name="status"
          label="Status"
          options={RoleSatus}
          value={
            RoleSatus.find(
              (role) => role.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(SubscribedMapForm);
